import {
  InstallationType,
  useGetSolutionsQuery,
} from "components/EtudeForm/hooks/useGetSolutionsQuery";
import { useEffect, useMemo, useState } from "react";
import { boilerTypeArrayToApiNumber } from "utils/helpers/boilerType";
import useTranslation from "utils/hooks/useTranslation";

export const usePreferencePacOptions = ({
  appliedSolutionFilter,
  type,
  preference_pac,
  code_gamme_pac,
  traitement_anti_corrosion,
  currentSolutions,
}: {
  appliedSolutionFilter?: GetSolutionRequest | null;
  type?: InstallationType;
  preference_pac?: string[];
  code_gamme_pac: string[];
  nombre_de_pac?: string;
  traitement_anti_corrosion?: boolean;
  currentSolutions?: Solution[];
}) => {
  const { t } = useTranslation();
  const { getHybrideSolutions, getElectricSolutions } = useGetSolutionsQuery();
  const [unfilteredSolutions, setUnfilteredSolutions] = useState<Solution[]>(
    []
  );
  useEffect(() => {
    if (type && appliedSolutionFilter) {
      (async () => {
        try {
          const apiRequestBody: GetSolutionRequest = {
            ...appliedSolutionFilter,
            filtres: {
              ...(appliedSolutionFilter.filtres || {}),
              type_chaudiere: boilerTypeArrayToApiNumber(
                appliedSolutionFilter.filtres?.type_chaudiere
              ) as any,
              preference_pac: undefined,
            },
          };

          const result = await (type === InstallationType.electric
            ? getElectricSolutions
            : getHybrideSolutions)(apiRequestBody).unwrap();
          // dispatch(setAcoustiqueSolution(result.acoustique_solution ?? false));
          if (!result.solutions) {
            throw new Error();
          } else {
            return setUnfilteredSolutions(result.solutions);
          }
        } catch {
          setUnfilteredSolutions([]);
        }
      })();
    }
  }, [appliedSolutionFilter, type]);

  const preferencePacOptions = useMemo(() => {
    if (!appliedSolutionFilter) {
      return [];
    }

    const filteredSolutions = unfilteredSolutions
      .filter(
        (solution) =>
          (!code_gamme_pac ||
            code_gamme_pac.includes(
              solution.pac.hp_code_gamme_pac as string
            )) &&
          solution.pac.hp_name_engine && solution.pac.thercodim_active
      );

    const solutionOptions = filteredSolutions.map(
      (solution) => solution.pac.hp_name_engine!
    );
    const labelsByEngineName = new Map([
      ...(currentSolutions ?? []).map((solution) => [
        solution.pac.hp_name_engine as string,
        solution.pac.nom,
      ] as [string, string]),
      ...filteredSolutions.map((solution) => [
        solution.pac.hp_name_engine as string,
        solution.pac.nom,
      ] as [string, string]),
    ]);

    //TOCHECK Mariam: We changed the logic to show pac.nom as label not hp_name_engine, 
    // can you please check this preference_pac part, I can't get case where we show  preference_pac-s as filter 
    const options = [
      ...(preference_pac?.filter((v) => !solutionOptions.includes(v)) ?? []),
      ...solutionOptions,
    ].filter((item, i: number, self) => self.indexOf(item) === i);

    return (options ?? [])
      .map((v: string) => {
        const getLabel = (ac?: string, checkEmpty?: boolean) =>
          t(`${ac}${v?.toUpperCase()}`, undefined, undefined, checkEmpty);
        let acPart = traitement_anti_corrosion ? "AC_" : "";
        if (acPart && !getLabel(acPart, true)) {
          acPart = "";
        }

        return {
          value: v,
          label: labelsByEngineName.get(v) || getLabel(acPart),
        };
      })
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      );
  }, [
    unfilteredSolutions, currentSolutions, preference_pac,
    code_gamme_pac,
    traitement_anti_corrosion,
    type,
    t,
  ]);

  return preferencePacOptions;
};
